import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "lucide-react";
import { Job } from "./JobBoard";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";

interface EditJobDialogProps {
  job: Job | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onJobUpdated: (updatedJob?: Job) => void;
  isDarkMode?: boolean;
}

export function EditJobDialog({ job, open, onOpenChange, onJobUpdated, isDarkMode = true }: EditJobDialogProps) {
  const [jobUrl, setJobUrl] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [description, setDescription] = useState("");
  const [deadline, setDeadline] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    if (job) {
      setJobUrl(job.url);
      setCompanyName(job.company_name);
      setJobTitle(job.job_title);
      setDescription(job.description);
      setDeadline(job.deadline || "");
    }
  }, [job]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!job) return;

    // Create an updated job object with the new values
    const updatedJobData = {
      ...job,
      url: jobUrl,
      company_name: companyName,
      job_title: jobTitle,
      description,
      deadline: deadline || null,
    };

    const { error } = await supabase
      .from('jobs')
      .update({
        url: jobUrl,
        company_name: companyName,
        job_title: jobTitle,
        description,
        deadline: deadline || null,
      })
      .eq('id', job.id);

    if (error) {
      toast({
        title: "Error updating job",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Job updated",
      description: "The job has been updated successfully!",
    });

    // Close the dialog and pass the updated job data to the parent component
    onOpenChange(false);
    onJobUpdated(updatedJobData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className={cn(
          "sm:max-w-[425px]",
          isDarkMode ? "bg-[#2C2E2D] border-[#818881] text-[#D9D9D9]" : "bg-white"
        )}
      >
        <DialogHeader>
          <DialogTitle className={isDarkMode ? "text-[#D9D9D9]" : "text-gray-900"}>
            Edit Job
          </DialogTitle>
          <DialogDescription className={isDarkMode ? "text-[#818881]" : "text-gray-500"}>
            Make changes to the job details below.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="url" className={isDarkMode ? "text-[#D9D9D9]" : ""}>Job URL</Label>
              <Input
                id="url"
                value={jobUrl}
                onChange={(e) => setJobUrl(e.target.value)}
                placeholder="https://..."
                className={isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9] placeholder:text-[#818881]" : ""}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="company" className={isDarkMode ? "text-[#D9D9D9]" : ""}>Company Name</Label>
              <Input
                id="company"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className={isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9]" : ""}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="title" className={isDarkMode ? "text-[#D9D9D9]" : ""}>Job Title</Label>
              <Input
                id="title"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className={isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9]" : ""}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description" className={isDarkMode ? "text-[#D9D9D9]" : ""}>Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9]" : ""}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="deadline" className={isDarkMode ? "text-[#D9D9D9]" : ""}>Application Deadline</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="deadline"
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className={cn("pl-10", isDarkMode ? "bg-[#818881]/20 border-[#818881] text-[#D9D9D9]" : "")}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="submit"
              className={isDarkMode ? "bg-[#CCF751] text-[#2C2E2D] hover:bg-[#CCF751]/90" : ""}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
} 